// Generated by TaroBase SDK Generator: 2026-04-14T11:42:05.668Z
// Collection: dangerRoomMessages/$messageId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Chat messages for the Danger Room feature. Messages belong to a channel (public, holders, whale, core-team). Users can send text, images, and GIFs. Admin, founder, and team members can delete any message for moderation.
 */

export interface DangerRoomMessagesRequest {
  channel: string;
  senderAddress: string;
  displayName: string;
  message?: string;
  imageUrl?: string;
  gifUrl?: string;
  timestamp: number | TimeOperation | IncrementOperation | TokenAmount;
  mentions?: string;
  stickerUrl?: string;
}

export interface DangerRoomMessagesResponse {
  channel: string;
  senderAddress: string;
  displayName: string;
  message?: string;
  imageUrl?: string;
  gifUrl?: string;
  timestamp: number;
  mentions?: string;
  stickerUrl?: string;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a DangerRoomMessages operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDangerRoomMessages(messageId: string, data?: DangerRoomMessagesRequest): DocumentOperation {
  const doc = data ? transformValues(data) : {};
  return { path: `dangerRoomMessages/${messageId}`, document: doc };
}

/**
 * Authenticated users only. senderAddress must match the caller's wallet address. All fields are immutable after creation. channel must be one of: public, holders, whale, core-team (enforced by frontend). message is optional since a message might only contain an image or GIF. mentions is optional and stores a comma-separated list of wallet addresses that were @mentioned in the message (e.g., 'addr1,addr2'). Since update is disallowed, mentions is effectively immutable after creation. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setDangerRoomMessages(messageId: string, data?: DangerRoomMessagesRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data || {});
    const succeeded = await set(`dangerRoomMessages/${messageId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting DangerRoomMessages: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read messages. Channel-based access gating is enforced on the frontend, not in policy rules.
   (Get Single Item)
 */
export async function getDangerRoomMessages(messageId: string): Promise<DangerRoomMessagesResponse | null> {
  try {
    const result = await get(`dangerRoomMessages/${messageId}`);
    return result as DangerRoomMessagesResponse | null;
  } catch (error) {
    console.error(`Error getting DangerRoomMessages: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single DangerRoomMessages document. (
  Read Operation Details: Anyone can read messages. Channel-based access gating is enforced on the frontend, not in policy rules.
  )
 */
export function subscribeDangerRoomMessages(callback: (data: DangerRoomMessagesResponse | null) => void, messageId: string): Promise<() => Promise<void>> {
  return subscribe(`dangerRoomMessages/${messageId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as DangerRoomMessagesResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to DangerRoomMessages at path '${`dangerRoomMessages/${messageId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many DangerRoomMessages items from collection dangerRoomMessages
 
  Read Operation Details: Anyone can read messages. Channel-based access gating is enforced on the frontend, not in policy rules.
  
 */
export async function getManyDangerRoomMessages(filter: string = ""): Promise<DangerRoomMessagesResponse[]> {
  try {
    const results = await get("dangerRoomMessages", { prompt: filter });
    return Array.isArray(results) ? results as DangerRoomMessagesResponse[] : [];
  } catch (error) {
    console.error(`Error getting DangerRoomMessages collection at path 'dangerRoomMessages': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in DangerRoomMessages collection at dangerRoomMessages
 
  Read Operation Details: Anyone can read messages. Channel-based access gating is enforced on the frontend, not in policy rules.
  
 */
export function subscribeManyDangerRoomMessages(callback: (data: DangerRoomMessagesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("dangerRoomMessages", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as DangerRoomMessagesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to DangerRoomMessages collection at path '${"dangerRoomMessages"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all DangerRoomMessages items from collection dangerRoomMessages
 
  Read Operation Details: Anyone can read messages. Channel-based access gating is enforced on the frontend, not in policy rules.
  
 */
export async function getAllDangerRoomMessages(filter: string = ""): Promise<DangerRoomMessagesResponse[]> {
  try {
    const results = await get("dangerRoomMessages", { prompt: filter });
    return Array.isArray(results) ? results as DangerRoomMessagesResponse[] : [];
  } catch (error) {
    console.error(`Error getting DangerRoomMessages collection at path 'dangerRoomMessages': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in DangerRoomMessages collection at dangerRoomMessages
 
  Read Operation Details: Anyone can read messages. Channel-based access gating is enforced on the frontend, not in policy rules.
  
 */
export function subscribeAllDangerRoomMessages(callback: (data: DangerRoomMessagesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("dangerRoomMessages", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as DangerRoomMessagesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to DangerRoomMessages collection at path '${"dangerRoomMessages"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count DangerRoomMessages items in collection dangerRoomMessages.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countDangerRoomMessages(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("dangerRoomMessages", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting DangerRoomMessages at path 'dangerRoomMessages': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on DangerRoomMessages items in collection dangerRoomMessages.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateDangerRoomMessages(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("dangerRoomMessages", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating DangerRoomMessages at path 'dangerRoomMessages': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: The sender can delete their own message (senderAddress matches caller). Admin or founder can delete any message. Any wallet that exists in the teamMembers collection can also delete any message (checked via get(/teamMembers/@user.address) != null). This enables team-based moderation.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteDangerRoomMessages(messageId: string): Promise<boolean> {
  try {
    const succeeded = await set(`dangerRoomMessages/${messageId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting DangerRoomMessages: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for DangerRoomMessages for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteDangerRoomMessages(messageId: string): DocumentOperation {
  return { path: `dangerRoomMessages/${messageId}`, document: null };
}

